// AuthInterceptor.js - Global token expiration handler
class AuthInterceptor {
  constructor() {
    this.isInitialized = false;
    this.navigate = null;
    this.isLoggingOut = false; // Prevent multiple logout attempts
    this.originalFetch = null; // Store original fetch reference
    this.logoutTimer = null; // Store timeout reference
  }

  init(navigate) {
    // Always update navigate function, even if already initialized
    this.navigate = navigate;
    
    if (this.isInitialized) {
      console.log('🔄 AuthInterceptor updated with new navigate function');
      return;
    }
    
    this.setupFetchInterceptor();
    this.isInitialized = true;
    
    console.log('🔒 AuthInterceptor initialized - All API calls will auto-handle token expiration');
  }

  // Method to initialize just the fetch interceptor without navigation
  initEarly() {
    if (this.isInitialized) return;
    
    this.setupFetchInterceptor();
    this.isInitialized = true;
    
    console.log('⚡ AuthInterceptor initialized early - Ready to catch API calls');
  }

  handleLogout() {
    if (this.isLoggingOut) {
      console.warn('🚪 Logout already in progress, ignoring duplicate call');
      return; // Prevent multiple executions
    }
    
    // Clear any existing logout timer
    if (this.logoutTimer) {
      console.warn('⏰ Clearing existing logout timer');
      clearTimeout(this.logoutTimer);
    }
    
    this.isLoggingOut = true;
    const startTime = new Date();

    console.warn('🚪 Token expired - initiating logout sequence');
    console.warn('⏰ Starting 3-second countdown before redirect...');
    console.warn('🕒 Logout start time:', startTime.toLocaleTimeString());

    // Show logout notification first with countdown
    this.showLogoutNotificationWithCountdown();
    
    // Store the timer reference and wait exactly 3 seconds
    this.logoutTimer = setTimeout(() => {
      const endTime = new Date();
      const timeDiff = endTime - startTime;
      
      console.warn('🧹 3 seconds elapsed - now clearing storage and redirecting to login...');
      console.warn('🕒 Redirect time:', endTime.toLocaleTimeString());
      console.warn('⏱️ Total elapsed time:', timeDiff + 'ms');
      
      // Store timing info before clearing (for debugging)
      const logInfo = {
        start: startTime.toISOString(),
        end: endTime.toISOString(),
        duration: timeDiff
      };
      
      try {
        sessionStorage.setItem('logout_debug', JSON.stringify(logInfo));
      } catch (e) {
        // Ignore if can't set
      }
      
      // Clear all storage AFTER storing debug info
      localStorage.clear();
      sessionStorage.removeItem('logout_debug'); // Keep only this debug info
      
      // Use window.location for immediate redirect
      console.warn('📍 Forcing page redirect to /Login');
      window.location.replace('/Login');
      
      // Reset flags
      this.isLoggingOut = false;
      this.logoutTimer = null;
    }, 3000);
    
    console.warn('⏰ Logout timer set with ID:', this.logoutTimer);
  }

  showLogoutNotification() {
    // Create modern toast notification for session expiration
    const notification = document.createElement('div');
    notification.innerHTML = `
      <div style="
        position: fixed; 
        top: 20px; 
        right: 20px; 
        background: linear-gradient(135deg, #ff6b6b, #ee5a24); 
        color: white; 
        padding: 16px 24px; 
        border-radius: 12px; 
        z-index: 99999;
        box-shadow: 0 8px 32px rgba(238, 90, 36, 0.3);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        font-weight: 500;
        max-width: 320px;
        min-width: 280px;
        animation: slideInBounce 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.1);
      ">
        <div style="display: flex; align-items: center; gap: 12px;">
          <div style="
            font-size: 24px;
            animation: pulse 2s infinite;
            filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.3));
          ">🔒</div>
          <div style="flex: 1;">
            <div style="
              font-weight: 700; 
              margin-bottom: 6px; 
              font-size: 15px;
              letter-spacing: 0.3px;
            ">Session Expired</div>
            <div style="
              font-size: 12px; 
              opacity: 0.95;
              line-height: 1.3;
              color: rgba(255, 255, 255, 0.9);
            ">Your session has expired. Redirecting to login...</div>
          </div>
        </div>
      </div>
    `;
    
    // Add animation keyframes if not already added
    if (!document.getElementById('auth-interceptor-styles')) {
      const style = document.createElement('style');
      style.id = 'auth-interceptor-styles';
      style.textContent = `
        @keyframes slideInBounce {
          0% { 
            transform: translateX(120%) scale(0.8); 
            opacity: 0; 
          }
          50% { 
            transform: translateX(-10%) scale(1.05); 
            opacity: 0.9; 
          }
          100% { 
            transform: translateX(0) scale(1); 
            opacity: 1; 
          }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `;
      document.head.appendChild(style);
    }
    
    document.body.appendChild(notification);
    
    // Remove notification after 2.8 seconds (just before logout)
    setTimeout(() => {
      if (notification.parentNode) {
        document.body.removeChild(notification);
      }
    }, 2800);
  }

  showLogoutNotificationWithCountdown() {
    // Create modern toast notification with countdown
    const notification = document.createElement('div');
    let countdown = 3;
    
    const updateNotification = () => {
      notification.innerHTML = `
        <div style="
          position: fixed; 
          top: 20px; 
          right: 20px; 
          background: linear-gradient(135deg, #ff6b6b, #ee5a24); 
          color: white; 
          padding: 16px 24px; 
          border-radius: 12px; 
          z-index: 99999;
          box-shadow: 0 8px 32px rgba(238, 90, 36, 0.3);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
          font-weight: 500;
          max-width: 320px;
          min-width: 280px;
          animation: slideInBounce 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        ">
          <div style="display: flex; align-items: center; gap: 12px;">
            <div style="
              font-size: 24px;
              animation: pulse 2s infinite;
              filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.3));
            ">🔒</div>
            <div style="flex: 1;">
              <div style="
                font-weight: 700; 
                margin-bottom: 6px; 
                font-size: 15px;
                letter-spacing: 0.3px;
              ">Session Expired</div>
              <div style="
                font-size: 12px; 
                opacity: 0.95;
                line-height: 1.3;
                color: rgba(255, 255, 255, 0.9);
              ">Redirecting to login in ${countdown} seconds...</div>
            </div>
            <div style="
              background: rgba(255, 255, 255, 0.2);
              border-radius: 50%;
              width: 32px;
              height: 32px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: 700;
              font-size: 16px;
            ">${countdown}</div>
          </div>
        </div>
      `;
    };

    // Add animation keyframes if not already added
    if (!document.getElementById('auth-interceptor-styles')) {
      const style = document.createElement('style');
      style.id = 'auth-interceptor-styles';
      style.textContent = `
        @keyframes slideInBounce {
          0% { 
            transform: translateX(120%) scale(0.8); 
            opacity: 0; 
          }
          50% { 
            transform: translateX(-10%) scale(1.05); 
            opacity: 0.9; 
          }
          100% { 
            transform: translateX(0) scale(1); 
            opacity: 1; 
          }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `;
      document.head.appendChild(style);
    }
    
    updateNotification();
    document.body.appendChild(notification);
    
    // Update countdown every second
    const countdownInterval = setInterval(() => {
      countdown--;
      if (countdown > 0) {
        updateNotification();
      } else {
        clearInterval(countdownInterval);
      }
    }, 1000);
    
    // Remove notification after 2.8 seconds (just before logout)
    setTimeout(() => {
      if (notification.parentNode) {
        document.body.removeChild(notification);
      }
      clearInterval(countdownInterval);
    }, 2800);
  }

  setupFetchInterceptor() {
    // Store original fetch to avoid infinite loops
    this.originalFetch = window.fetch.bind(window);

    // Override global fetch
    window.fetch = async (...args) => {
      const [url, options = {}] = args;
      console.log(`🌐 Intercepted API call: ${url}`);
      
      try {
        const response = await this.originalFetch.apply(window, args);
        
        console.log(`📡 Response status: ${response.status} for ${url}`);
        
        // Handle different HTTP status codes
        if (response.status === 401) {
          console.warn('🚫 HTTP 401 Unauthorized - Token expired or invalid');
          console.warn('🔓 Triggering automatic logout...');
          this.handleLogout();
          // Don't throw here - let component handle response if needed
          return response;
        }
        
        if (response.status === 403) {
          console.warn('🚫 HTTP 403 Forbidden - Access denied or token expired');
          console.warn('🔓 Triggering automatic logout in 3 seconds...');
          console.warn('🕒 Current time:', new Date().toLocaleTimeString());
          this.handleLogout();
          // Don't throw here - let component handle response if needed
          return response;
        }
        
        return response;
      } catch (error) {
        console.log(`❌ Fetch error for ${url}:`, error.message);
        
        // Re-throw network errors without interfering
        throw error;
      }
    };
    
    console.log('✅ Fetch interceptor is now active');
  }

  // Optional: Method to manually trigger logout (for logout buttons)
  manualLogout() {
    console.log('🔓 Manual logout triggered - immediate redirect');
    // For manual logout, clear storage and redirect immediately
    localStorage.clear();
    sessionStorage.clear();
    window.location.replace('/Login');
  }

  // Optional: Method to cancel pending logout (if needed)
  cancelLogout() {
    if (this.logoutTimer) {
      console.log('❌ Cancelling pending logout');
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
      this.isLoggingOut = false;
    }
  }

  // Optional: Method to restore original fetch (for cleanup/testing)
  restore() {
    if (this.originalFetch) {
      window.fetch = this.originalFetch;
      this.isInitialized = false;
      console.log('🔄 AuthInterceptor restored original fetch');
    }
  }

  // Method to check if interceptor is active
  isActive() {
    return this.isInitialized;
  }
}

// Export singleton instance
export const authInterceptor = new AuthInterceptor();